import axios from 'axios';
import { API_BASE } from './config';

/**
 * Job type enum
 */
export enum JobType {
  WAITING = 'waiting',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  DELAYED = 'delayed'
}

/**
 * Job interface
 */
export interface Job {
  id: string;
  data: any;
  opts: any;
  progress: number;
  attemptsMade: number;
  timestamp: number;
  processedOn?: number;
  finishedOn?: number;
  failedReason?: string;
}

/**
 * Pagination interface
 */
export interface Pagination {
  total: number;
  limit: number;
  offset: number;
}

/**
 * Fetch queue metrics
 * @returns Current metrics for all queues
 */
export const fetchQueueMetrics = async () => {
  try {
    const response = await axios.get(`${API_BASE}/queue/metrics`, {
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching queue metrics:', error);
    // Return a default structure to prevent UI errors
    return {
      balanceQueue: { waiting: 0, active: 0, completed: 0, failed: 0, delayed: 0 },
      transactionQueue: { waiting: 0, active: 0, completed: 0, failed: 0, delayed: 0 },
      callbackQueue: { waiting: 0, active: 0, completed: 0, failed: 0, delayed: 0 }
    };
  }
};

/**
 * Fetch queue metrics history
 * @param limit Maximum number of data points to return
 * @returns Historical metrics for all queues
 */
export const fetchQueueHistory = async (limit: number = 10) => {
  try {
    const response = await axios.get(`${API_BASE}/queue/history`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching queue history:', error);
    // Return a default structure to prevent UI errors
    return { history: [] };
  }
};

/**
 * Fetch deposit queue statistics
 * @returns Statistics for deposit queues
 */
export const fetchDepositQueueStats = async () => {
  try {
    const response = await axios.get(`${API_BASE}/queue/deposit/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching deposit queue stats:', error);
    // Return a default structure to prevent UI errors
    return { queues: [] };
  }
};

/**
 * Fetch withdrawal queue statistics
 * @returns Statistics for withdrawal queues
 */
export const fetchWithdrawalQueueStats = async () => {
  try {
    const response = await axios.get(`${API_BASE}/queue/withdrawal/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching withdrawal queue stats:', error);
    // Return a default structure to prevent UI errors
    return { queues: [] };
  }
};

/**
 * Fetch webhook queue statistics
 * @returns Statistics for webhook queues
 */
export const fetchWebhookQueueStats = async () => {
  try {
    const response = await axios.get(`${API_BASE}/queue/webhook/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching webhook queue stats:', error);
    // Return a default structure to prevent UI errors
    return { queues: [] };
  }
};

/**
 * Fetch sweep queue statistics
 * @returns Statistics for sweep queues
 */
export const fetchSweepQueueStats = async () => {
  try {
    const response = await axios.get(`${API_BASE}/queue/sweep/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sweep queue stats:', error);
    // Return a default structure to prevent UI errors
    return { queues: [] };
  }
};

/**
 * Fetch sweep historical statistics from database
 * @returns Historical statistics for sweep operations
 */
export const fetchSweepHistory = async () => {
  try {
    const response = await axios.get(`${API_BASE}/queue/sweep/history`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sweep history:', error);
    // Return a default structure to prevent UI errors
    return {
      stats: {
        total_swept: 0,
        completed: 0,
        failed: 0,
        not_needed: 0,
        last_24_hours: 0,
        last_7_days: 0,
        success_rate: 0
      },
      recent_sweeps: []
    };
  }
};

/**
 * Fetch failed webhook jobs
 * @param limit Maximum number of jobs to return
 * @param offset Offset for pagination
 * @returns Failed webhook jobs
 */
export const fetchFailedWebhookJobs = async (limit: number = 10, offset: number = 0) => {
  try {
    const response = await axios.get(`${API_BASE}/queue/webhook/failed`, {
      params: { limit, offset }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching failed webhook jobs:', error);
    // Return a default structure to prevent UI errors
    return { jobs: [] };
  }
};

/**
 * Retry a failed webhook job
 * @param id Job ID
 * @returns Operation result
 */
export const retryWebhookJob = async (id: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/webhook/retry/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error retrying webhook job:', error);
    throw error;
  }
};

/**
 * Retry all failed webhook jobs
 * @returns Operation result
 */
export const retryAllWebhookJobs = async () => {
  try {
    const response = await axios.post(`${API_BASE}/queue/webhook/retry-all`);
    return response.data;
  } catch (error) {
    console.error('Error retrying all webhook jobs:', error);
    throw error;
  }
};

/**
 * Pause a queue
 * @param queueName Queue name
 * @returns Operation result
 */
export const pauseQueue = async (queueName: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/pause`, { queue: queueName });
    return response.data;
  } catch (error) {
    console.error(`Error pausing queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Resume a queue
 * @param queueName Queue name
 * @returns Operation result
 */
export const resumeQueue = async (queueName: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/resume`, { queue: queueName });
    return response.data;
  } catch (error) {
    console.error(`Error resuming queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Process waiting jobs in a queue
 * @param queueName Queue name
 * @returns Operation result
 */
export const processQueue = async (queueName: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/process`, { queue: queueName });
    return response.data;
  } catch (error) {
    console.error(`Error processing queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Clear failed jobs in a queue
 * @param queueName Queue name
 * @returns Operation result
 */
export const clearFailedJobs = async (queueName: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/clear-failed`, { queue: queueName });
    return response.data;
  } catch (error) {
    console.error(`Error clearing failed jobs for queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Fetch jobs from a queue
 * @param queueName Queue name
 * @param type Job type (waiting, active, completed, failed, delayed)
 * @param limit Maximum number of jobs to return
 * @param offset Offset for pagination
 * @returns Jobs from the queue
 */
export const fetchQueueJobs = async (
  queueName: string,
  type: JobType,
  limit: number = 10,
  offset: number = 0
) => {
  try {
    const response = await axios.get(`${API_BASE}/queue/jobs`, {
      params: { queue: queueName, type, limit, offset }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${type} jobs for queue ${queueName}:`, error);
    // Return a default structure to prevent UI errors
    return {
      success: false,
      jobs: [],
      pagination: { total: 0, limit, offset },
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Promote a job to the front of the queue
 * @param queueName Queue name
 * @param jobId Job ID
 * @returns API response
 */
export const promoteJob = async (queueName: string, jobId: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/jobs/promote`, { queue: queueName, jobId });
    return response.data;
  } catch (error) {
    console.error(`Error promoting job ${jobId} in queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Delete a job from the queue
 * @param queueName Queue name
 * @param jobId Job ID
 * @returns API response
 */
export const deleteJob = async (queueName: string, jobId: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/jobs/delete`, { queue: queueName, jobId });
    return response.data;
  } catch (error) {
    console.error(`Error deleting job ${jobId} from queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Change job priority
 * @param queueName Queue name
 * @param jobId Job ID
 * @param priority Priority value (1-10, lower is higher priority)
 * @returns API response
 */
export const changeJobPriority = async (queueName: string, jobId: string, priority: number) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/jobs/priority`, { queue: queueName, jobId, priority });
    return response.data;
  } catch (error) {
    console.error(`Error changing priority for job ${jobId} in queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Retry a failed job
 * @param queueName Queue name
 * @param jobId Job ID
 * @returns API response
 */
export const retryJob = async (queueName: string, jobId: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/jobs/retry`, { queue: queueName, jobId });
    return response.data;
  } catch (error) {
    console.error(`Error retrying job ${jobId} in queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Duplicate a job
 * @param queueName Queue name
 * @param jobId Job ID
 * @returns API response
 */
export const duplicateJob = async (queueName: string, jobId: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/jobs/duplicate`, { queue: queueName, jobId });
    return response.data;
  } catch (error) {
    console.error(`Error duplicating job ${jobId} in queue ${queueName}:`, error);
    throw error;
  }
};

/**
 * Move a job to another queue
 * @param sourceQueue Source queue name
 * @param targetQueue Target queue name
 * @param jobId Job ID
 * @returns API response
 */
export const moveJob = async (sourceQueue: string, targetQueue: string, jobId: string) => {
  try {
    const response = await axios.post(`${API_BASE}/queue/jobs/move`, {
      sourceQueue,
      targetQueue,
      jobId
    });
    return response.data;
  } catch (error) {
    console.error(`Error moving job ${jobId} from queue ${sourceQueue} to queue ${targetQueue}:`, error);
    throw error;
  }
};

/**
 * Fetch failed transactions cache statistics
 * @returns Statistics for transactions that have recently failed to enqueue
 */
export const fetchFailedTransactionsStats = async () => {
  try {
    const response = await axios.get(`${API_BASE}/queue/withdrawal/failed-transactions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching failed transactions statistics:', error);
    // Return a default structure to prevent UI errors
    return {
      success: false,
      stats: {
        count: 0,
        byStatus: {},
        transactions: []
      },
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Clear failed transactions cache
 * @returns Operation result
 */
export const clearFailedTransactionsCache = async () => {
  try {
    const response = await axios.post(`${API_BASE}/queue/withdrawal/clear-failed-transactions`);
    return response.data;
  } catch (error) {
    console.error('Error clearing failed transactions cache:', error);
    throw error;
  }
};

/**
 * Search for a job by ID across all queues
 * @param jobId Job ID to search for
 * @returns API response with the job if found
 */
export const searchJobById = async (jobId: string) => {
  try {
    const response = await axios.get(`${API_BASE}/queue/jobs/search`, {
      params: { jobId }
    });
    return response.data;
  } catch (error) {
    console.error(`Error searching for job ${jobId}:`, error);
    throw error;
  }
};
